import CarouselArrowL from "@/assets/icons/CarouselArrowL";
import CarouselArrowR from "@/assets/icons/CarouselArrowR";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { OurStoryOfGrowth as IOurStoryOfGrowth } from "@/types/about-us-page";
import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

const OurStoryOfGrowth: React.FC<{
  ourStoryOfGrowth?: IOurStoryOfGrowth[];
  isLoading: boolean;
}> = ({ ourStoryOfGrowth, isLoading }) => {
  const { i18n } = useTranslation();

  const isRTL = i18n.language === "ar";

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const tabValues = useMemo(
    () => ourStoryOfGrowth?.map((tab) => tab.year) || [],
    [ourStoryOfGrowth],
  );
  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    if (tabValues.length > 0) {
      setActiveTab(tabValues[0]);
    }
  }, [tabValues]);

  const scrollToActiveTab = useCallback((tabValue: string) => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) {
      return;
    }

    const tabButton = scrollArea.querySelector(`#radix-\\:r4\\:-trigger-${tabValue}`);
    if (!tabButton) {
      return;
    }

    const scrollContainer = scrollArea.querySelector("[data-radix-scroll-area-viewport]");
    if (!scrollContainer) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const buttonRect = tabButton.getBoundingClientRect();

    // Calculate if button is outside the visible area
    const isButtonOutsideLeft = buttonRect.left < containerRect.left;
    const isButtonOutsideRight = buttonRect.right > containerRect.right;

    if (isButtonOutsideLeft || isButtonOutsideRight) {
      // Scroll to center the button
      const scrollLeft =
        buttonRect.left -
        containerRect.left +
        scrollContainer.scrollLeft -
        containerRect.width / 2 +
        buttonRect.width / 2;
      scrollContainer.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, []);

  const goNext = useCallback(() => {
    const currentIndex = tabValues.indexOf(activeTab);
    if (currentIndex < tabValues.length - 1) {
      const nextTab = tabValues[currentIndex + 1];
      setActiveTab(nextTab);
      setTimeout(() => scrollToActiveTab(nextTab), 50);
    }
  }, [tabValues, activeTab, scrollToActiveTab]);

  const goPrev = useCallback(() => {
    const currentIndex = tabValues.indexOf(activeTab);
    if (currentIndex > 0) {
      const prevTab = tabValues[currentIndex - 1];
      setActiveTab(prevTab);
      setTimeout(() => scrollToActiveTab(prevTab), 50);
    }
  }, [tabValues, activeTab, scrollToActiveTab]);

  const canScrollPrev = useMemo(() => tabValues.indexOf(activeTab) > 0, [tabValues, activeTab]);
  const canScrollNext = useMemo(
    () => tabValues.indexOf(activeTab) < tabValues.length - 1,
    [tabValues, activeTab],
  );

  const handleValueChange = useCallback(
    (value: string) => {
      setActiveTab(value);
      setTimeout(() => scrollToActiveTab(value), 50);
    },
    [scrollToActiveTab],
  );
  return (
    <Tabs
      defaultValue={activeTab}
      onValueChange={handleValueChange}
      value={activeTab}
      className="w-full"
    >
      <ScrollArea
        ref={scrollAreaRef}
        className="w-full overflow-x-auto"
        orientation="horizontal"
        scrollbarClassName="hidden"
      >
        {isLoading ? (
          <Skeleton className="mb-8 h-12 w-full" />
        ) : (
          <TabsList className="inline-flex h-auto bg-transparent p-0 [&>button]:rounded-none [&>button]:font-thin [&>button]:text-foreground">
            {ourStoryOfGrowth?.map((tab) => (
              <TabsTrigger
                key={tab.year}
                value={tab.year}
                className="border-b-2 py-4 data-[state=active]:border-[#232329] data-[state=inactive]:border-[#CCC2BF] data-[state=active]:bg-transparent data-[state=inactive]:opacity-50 data-[state=active]:shadow-none md:w-[180px]"
              >
                {tab.year}
              </TabsTrigger>
            ))}
          </TabsList>
        )}
      </ScrollArea>
      {isLoading ? (
        <OurStoryOfGrowthSkeleton isRTL={isRTL} />
      ) : (
        ourStoryOfGrowth?.map((tab) => (
          <TabsContent key={tab.year} value={tab.year} className="mt-8">
            <div className="flex flex-col gap-6 overflow-hidden md:h-[365px] md:flex-row md:items-start">
              <div className="h-full w-full flex-1">
                <img
                  src={tab.image?.large_url}
                  alt={tab.title}
                  className="h-full w-full rounded-lg object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 space-y-4">
                <h5 className="text-xl font-semibold md:text-3xl">{tab.title}</h5>
                <p className="text-base leading-relaxed md:text-lg">{tab.description}</p>
                <div className="!mt-8 flex flex-row gap-5">
                  <div className="h-12 w-12">
                    <Button
                      size="icon"
                      className={cn(
                        "group bg-[#232329] hover:bg-[#fafafa] h-full w-full [&>svg>path]:fill-white hover:[&>svg>path]:fill-black ",
                        !canScrollPrev && "bg-[#CCC2BF] opacity-100",
                      )}
                      aria-label="Go to previous slide"
                      disabled={!canScrollPrev}
                      onClick={goPrev}
                      style={{ pointerEvents: canScrollPrev ? "auto" : "none" }}
                    >
                      {isRTL ? (
                        <CarouselArrowR className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
                      ) : (
                        <CarouselArrowL className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
                      )}
                    </Button>
                  </div>
                  <div className="h-12 w-12">
                    <Button
                      size="icon"
                      className={cn(
                        "group bg-[#232329] hover:bg-[#fafafa] h-full w-full [&>svg>path]:fill-white hover:[&>svg>path]:fill-black ",
                        !canScrollNext && "bg-[#CCC2BF] opacity-100",
                      )}
                      aria-label="Go to next slide"
                      disabled={!canScrollNext}
                      onClick={goNext}
                      style={{ pointerEvents: canScrollNext ? "auto" : "none" }}
                    >
                      {isRTL ? (
                        <CarouselArrowL className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
                      ) : (
                        <CarouselArrowR className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        ))
      )}
    </Tabs>
  );
};

export const OurStoryOfGrowthSkeleton: React.FC<{
  isRTL: boolean;
}> = ({ isRTL }) => {
  return (
    <div className="flex flex-col gap-6 overflow-hidden md:h-[365px] md:flex-row md:items-start">
      <div className="h-full w-full flex-1">
        <Skeleton className="h-44 w-full rounded-lg md:h-full" />
      </div>
      <div className="flex-1 space-y-4">
        <Skeleton className="h-8 w-52 rounded-lg md:h-12 md:w-72" />
        <Skeleton className="h-24 w-full rounded-lg md:h-52" />
        <div className="!mt-8 flex flex-row gap-5">
          <div className="h-12 w-12">
            <Button
              size="icon"
              className={
                "group h-full w-full bg-[#CCC2BF] opacity-100 hover:bg-[#fafafa] [&>svg>path]:fill-white hover:[&>svg>path]:fill-black"
              }
              aria-label="Go to previous slide"
              disabled
              style={{ pointerEvents: "none" }}
            >
              {isRTL ? (
                <CarouselArrowR className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
              ) : (
                <CarouselArrowL className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
              )}
            </Button>
          </div>
          <div className="h-12 w-12">
            <Button
              size="icon"
              className={
                "group h-full w-full bg-[#CCC2BF] opacity-100 hover:bg-[#fafafa] [&>svg>path]:fill-white hover:[&>svg>path]:fill-black"
              }
              aria-label="Go to next slide"
              disabled
              style={{ pointerEvents: "none" }}
            >
              {isRTL ? (
                <CarouselArrowL className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
              ) : (
                <CarouselArrowR className="[&>path]:fill-white group-hover:[&>path]:fill-black" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurStoryOfGrowth;
